import { get } from 'lodash';
import { baselightTheme } from '@shared/theme/DefaultColorsV2';
import type { AnalysisRecord } from '../config/types';
import type { ChartDefinition } from '../config/types';

export interface ChartDataItem {
  name: string;
  value: number;
  fill?: string;
}

const CHART_COLORS = baselightTheme.palette!.chart!;

function getFieldValue(record: AnalysisRecord, path: string): string {
  const v = get(record, path);
  if (v == null || v === '') return '—';
  return String(v);
}

export function getChartData(data: AnalysisRecord[], def: ChartDefinition): ChartDataItem[] {
  const excludeSet = new Set(def.excludeValues ?? []);
  const counts: Record<string, number> = {};

  if (def.groupBy) {
    // Segment chart: e.g. conversion rate by device_type
    const groups = new Map<string, AnalysisRecord[]>();
    data.forEach((r) => {
      const groupKey = getFieldValue(r, def.groupBy!);
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey)!.push(r);
    });
    const successValues = ['InstalledConfirmed', 'ExistingUserFeedbackCaptured'];
    const result: ChartDataItem[] = [];
    groups.forEach((records, groupName) => {
      const total = records.length;
      const success = records.filter((r) =>
        successValues.includes(String(get(r, def.field))),
      ).length;
      result.push({ name: groupName, value: total ? (success / total) * 100 : 0 });
    });
    return result
      .sort((a, b) => b.value - a.value)
      .slice(0, def.maxCategories ?? 20)
      .map((item, i) => ({ ...item, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  }

  data.forEach((r) => {
    const key = getFieldValue(r, def.field);
    if (excludeSet.has(key)) return;
    counts[key] = (counts[key] ?? 0) + 1;
  });

  const sorted = Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, def.maxCategories ?? 20);
  const rest = Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(def.maxCategories ?? 20);
  const restSum = rest.reduce((acc, [, n]) => acc + n, 0);
  const items: ChartDataItem[] = sorted.map(([name, value], i) => ({
    name,
    value,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));
  if (restSum > 0) {
    items.push({
      name: 'Other',
      value: restSum,
      fill: CHART_COLORS[CHART_COLORS.length - 1],
    });
  }
  return items;
}
